/**
 * LayersPanel — Photoshop-style stacking-order panel for overlay layers.
 *
 * Capabilities (V2):
 *  - Tree rendering: group layers ("📁") contain child layers via `parentUid`
 *  - Multi-select via Shift (range) and Cmd/Ctrl (toggle)
 *  - Drag-to-reorder via @dnd-kit/sortable; group children are reordered
 *    within their group
 *  - Eye / Lock cascade from the parent group to every child
 *  - Bulk action toolbar appears when >1 layer is selected
 *  - Each row carries a small live preview that mirrors the layer's actual
 *    look (text in real color/weight, real image thumbnail, SVG shape, etc.)
 *  - Scenes section underneath with live-frame Remotion thumbnails
 */
import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type {
  OverlayLayer,
  ReelScene,
  SceneMeta,
} from "../../api";
import { LiveSceneThumb } from "./LiveSceneThumb";

type Props = {
  overlays: OverlayLayer[];
  scenes: ReelScene[];
  scenesCatalog: SceneMeta[];
  format: string;
  selectedUid: string | null;
  selectedUids: Set<string>;
  /** Multi-select aware selection setter. */
  onSelect: (uid: string | null, mode: "replace" | "toggle" | "range", orderedUids?: string[]) => void;
  onOverlayPatch: (uid: string, patch: Partial<OverlayLayer>) => void;
  onReorderOverlays: (next: OverlayLayer[]) => void;
  onDuplicateOverlay: (uid: string) => void;
  onRemoveOverlay: (uid: string) => void;
  /** Bulk operations for the multi-select toolbar. */
  onBulkPatch: (uids: string[], patch: Partial<OverlayLayer>) => void;
  onBulkRemove: (uids: string[]) => void;
  onBulkDuplicate: (uids: string[]) => void;
  /** Group / ungroup. */
  onGroup: (uids: string[]) => void;
  onUngroup: (uids: string[]) => void;
  onSeekSec?: (sec: number) => void;
  /** Replace the entire scenes array — used for drag-reorder. */
  onReorderScenes?: (next: ReelScene[]) => void;
  /** Patch a scene's `inputs.extras` (hidden / locked / audio toggles). */
  onPatchSceneExtras?: (
    uid: string,
    patch: { hidden?: boolean; locked?: boolean; muteSfx?: boolean; muteMusic?: boolean },
  ) => void;
  onDuplicateScene?: (uid: string) => void;
  onRemoveScene?: (uid: string) => void;
};

/* ===================== helpers ===================== */

function overlayLabel(o: OverlayLayer): string {
  if (o.name && o.name.trim()) return o.name.trim();
  switch (o.kind) {
    case "text":
      return truncate(o.text || "Text", 28) || "Text";
    case "badge":
      return truncate(o.text || "Badge", 24) || "Badge";
    case "shape":
      return `Shape · ${o.shape}`;
    case "icon":
      return `Icon · ${o.icon}`;
    case "image": {
      const name = (o.src.split("/").pop() || "Bild").split("?")[0];
      return truncate(name, 28);
    }
    case "group":
      return "Gruppe";
  }
}

function overlayGlyph(o: OverlayLayer): string {
  switch (o.kind) {
    case "text": return "T";
    case "shape": return "▭";
    case "icon": return "✦";
    case "badge": return "●";
    case "image": return "🖼";
    case "group": return "📁";
  }
}

function truncate(s: string, n: number): string {
  if (!s) return "";
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

function categoryIcon(cat: SceneMeta["category"]): string {
  switch (cat) {
    case "intro": return "🎬";
    case "text": return "T";
    case "data": return "📊";
    case "list": return "≣";
    case "visual": return "✦";
    case "partner": return "🤝";
    case "outro": return "👋";
  }
}

const COLOR_TOKEN_MAP: Record<string, string> = {
  primary: "var(--color-primary, #04b4b4)",
  secondary: "var(--color-secondary, #f59e0b)",
  accent: "var(--color-accent, #fcd34d)",
  white: "#ffffff",
  black: "#000000",
};
function resolveAccent(c: string | undefined, fallback = "#e5e7eb"): string {
  if (!c) return fallback;
  return COLOR_TOKEN_MAP[c] ?? c;
}
function resolveLayerMedia(src: string): string {
  if (!src) return src;
  return src.trim();
}

/**
 * Re-assign `track` to overlays so the *display order* (top→bottom) matches
 * the requested z-order (front→back). Done per-bucket (root + each group)
 * so children inside a group don't fight with root-level layers for tracks.
 */
function restackTracks(displayOrderTopToBottom: OverlayLayer[]): OverlayLayer[] {
  const N = displayOrderTopToBottom.length;
  return displayOrderTopToBottom.map((o, i) => ({
    ...o,
    track: Math.max(0, Math.min(20, N - 1 - i)),
  }));
}

/* ===================== thumbnail ===================== */

const LayerThumb: React.FC<{ overlay: OverlayLayer }> = ({ overlay }) => {
  const o = overlay;
  const W = 40;
  const H = 28;

  if (o.kind === "image") {
    return (
      <div className="layer-thumb image">
        <img
          src={resolveLayerMedia(o.src)}
          alt=""
          loading="lazy"
          draggable={false}
          style={{ objectFit: o.fit ?? "cover", borderRadius: o.radius ? Math.min(8, o.radius / 4) : 0 }}
        />
      </div>
    );
  }
  if (o.kind === "text") {
    const color = resolveAccent(o.color, "#e5e7eb");
    return (
      <div className="layer-thumb text">
        <span
          style={{
            fontWeight: o.weight,
            color,
            fontSize: 12,
            lineHeight: 1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: W - 4,
            textShadow: o.shadow ? "0 1px 2px rgba(0,0,0,0.7)" : undefined,
            letterSpacing: o.letterSpacing ? `${o.letterSpacing}em` : undefined,
            textAlign: o.align,
          }}
        >
          {(o.text || "T").slice(0, 4)}
        </span>
      </div>
    );
  }
  if (o.kind === "badge") {
    const fill = resolveAccent(o.fill, "#1f2937");
    const fg = resolveAccent(o.textColor, "#ffffff");
    return (
      <div className="layer-thumb badge">
        <span
          style={{
            background: fill,
            color: fg,
            fontSize: 8,
            fontWeight: 700,
            padding: "2px 6px",
            borderRadius: 999,
            whiteSpace: "nowrap",
            maxWidth: W - 6,
            overflow: "hidden",
            textOverflow: "ellipsis",
            letterSpacing: 0.5,
          }}
        >
          {(o.text || "BADGE").slice(0, 6).toUpperCase()}
        </span>
      </div>
    );
  }
  if (o.kind === "shape") {
    const fill = resolveAccent(o.fill, "#6366f1");
    const stroke = resolveAccent(o.stroke, "transparent");
    const sw = o.strokeWidth ?? 0;
    const r = o.radius ?? 0;
    let shape: React.ReactNode = null;
    switch (o.shape) {
      case "rect":
        shape = <rect x={6} y={4} width={W - 12} height={H - 8} rx={Math.min(8, r / 4)} ry={Math.min(8, r / 4)} fill={fill} stroke={stroke} strokeWidth={sw ? 1 : 0} />;
        break;
      case "pill":
        shape = <rect x={4} y={6} width={W - 8} height={H - 12} rx={(H - 12) / 2} ry={(H - 12) / 2} fill={fill} stroke={stroke} strokeWidth={sw ? 1 : 0} />;
        break;
      case "circle":
        shape = <circle cx={W / 2} cy={H / 2} r={Math.min(W, H) / 2 - 3} fill={fill} stroke={stroke} strokeWidth={sw ? 1 : 0} />;
        break;
      case "star": {
        const cx = W / 2, cy = H / 2, r1 = 11, r2 = 4.5;
        const pts = Array.from({ length: 10 }, (_, i) => {
          const ang = -Math.PI / 2 + (i * Math.PI) / 5;
          const rr = i % 2 === 0 ? r1 : r2;
          return `${cx + Math.cos(ang) * rr},${cy + Math.sin(ang) * rr}`;
        }).join(" ");
        shape = <polygon points={pts} fill={fill} />;
        break;
      }
      case "arrow":
        shape = (
          <g fill={fill}>
            <rect x={4} y={H / 2 - 3} width={W - 14} height={6} />
            <polygon points={`${W - 14},${H / 2 - 8} ${W - 4},${H / 2} ${W - 14},${H / 2 + 8}`} />
          </g>
        );
        break;
      case "triangle":
        shape = <polygon points={`${W / 2},4 ${W - 4},${H - 4} 4,${H - 4}`} fill={fill} />;
        break;
    }
    return (
      <div className="layer-thumb shape">
        <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} xmlns="http://www.w3.org/2000/svg">
          {shape}
        </svg>
      </div>
    );
  }
  if (o.kind === "icon") {
    const color = resolveAccent(o.color, "#a78bfa");
    return (
      <div className="layer-thumb icon" style={{ color }}>
        <span style={{ fontSize: 18, lineHeight: 1 }}>✦</span>
      </div>
    );
  }
  if (o.kind === "group") {
    return (
      <div className="layer-thumb group">
        <span style={{ fontSize: 16, lineHeight: 1 }}>📁</span>
      </div>
    );
  }
  return <div className="layer-thumb" />;
};

/* ===================== row component ===================== */

type RowProps = {
  overlay: OverlayLayer;
  /** Indent level — 0 for root, 1 for direct child of a group. */
  indent: number;
  selected: boolean;
  /** Effective hidden/locked (after parent cascade). */
  effectivelyHidden: boolean;
  effectivelyLocked: boolean;
  /** For groups only: are we showing children? */
  expanded?: boolean;
  isGroup: boolean;
  childCount: number;
  onSelect: (e: React.MouseEvent) => void;
  onToggleHidden: () => void;
  onToggleLocked: () => void;
  onToggleExpanded?: () => void;
  onRename: (next: string) => void;
  onDuplicate: () => void;
  onRemove: () => void;
  onSeekToStart?: () => void;
};

const LayerRow: React.FC<RowProps> = ({
  overlay,
  indent,
  selected,
  effectivelyHidden,
  effectivelyLocked,
  expanded,
  isGroup,
  childCount,
  onSelect,
  onToggleHidden,
  onToggleLocked,
  onToggleExpanded,
  onRename,
  onDuplicate,
  onRemove,
  onSeekToStart,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: overlay.uid });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    paddingLeft: 8 + indent * 18,
  };

  const [editing, setEditing] = useState(false);
  const labelRef = useRef<HTMLInputElement | null>(null);
  const label = overlayLabel(overlay);
  const hidden = !!overlay.hidden;
  const locked = !!overlay.locked;

  const startRename = useCallback(() => {
    setEditing(true);
    setTimeout(() => {
      labelRef.current?.focus();
      labelRef.current?.select();
    }, 0);
  }, []);

  const commitRename = useCallback(
    (raw: string) => {
      const next = raw.trim();
      if (overlay.kind === "text" || overlay.kind === "badge") {
        if (!overlay.name) {
          onRename(next || (overlay.kind === "text" ? overlay.text : overlay.text));
        } else {
          onRename(next);
        }
      } else {
        onRename(next);
      }
      setEditing(false);
    },
    [overlay, onRename],
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={[
        "layer-row",
        selected ? "is-selected" : "",
        hidden ? "is-hidden" : "",
        locked ? "is-locked" : "",
        effectivelyHidden && !hidden ? "is-hidden-via-parent" : "",
        effectivelyLocked && !locked ? "is-locked-via-parent" : "",
        isGroup ? "is-group" : "",
      ].filter(Boolean).join(" ")}
      data-uid={overlay.uid}
      onClick={(e) => {
        if (editing) return;
        e.stopPropagation();
        onSelect(e);
        onSeekToStart?.();
      }}
    >
      <button
        type="button"
        className="layer-grip"
        title="Ziehen zum Umsortieren"
        aria-label="Drag-Handle"
        {...attributes}
        {...listeners}
      >
        ⋮⋮
      </button>

      {/* Group expand/collapse caret. Non-groups get a placeholder for grid alignment. */}
      {isGroup ? (
        <button
          type="button"
          className={`layer-caret ${expanded ? "open" : ""}`}
          onClick={(e) => { e.stopPropagation(); onToggleExpanded?.(); }}
          title={expanded ? "Gruppe einklappen" : "Gruppe ausklappen"}
          aria-label="Gruppe ausklappen"
        >
          {expanded ? "▾" : "▸"}
        </button>
      ) : (
        <span className="layer-caret-placeholder" />
      )}

      <button
        type="button"
        className={`layer-eye ${effectivelyHidden ? "is-off" : ""}`}
        onClick={(e) => { e.stopPropagation(); onToggleHidden(); }}
        title={effectivelyHidden ? "Layer einblenden" : "Layer ausblenden"}
        aria-label={effectivelyHidden ? "Einblenden" : "Ausblenden"}
      >
        {effectivelyHidden ? "🚫" : "👁"}
      </button>

      <button
        type="button"
        className={`layer-lock ${effectivelyLocked ? "is-on" : ""}`}
        onClick={(e) => { e.stopPropagation(); onToggleLocked(); }}
        title={effectivelyLocked ? "Sperre aufheben" : "Layer sperren"}
        aria-label={effectivelyLocked ? "Entsperren" : "Sperren"}
      >
        {effectivelyLocked ? "🔒" : "🔓"}
      </button>

      <LayerThumb overlay={overlay} />
      <span className="layer-glyph" aria-hidden title={overlay.kind}>{overlayGlyph(overlay)}</span>

      {editing ? (
        <input
          ref={labelRef}
          className="layer-name-input"
          defaultValue={
            overlay.name ??
            (overlay.kind === "text" || overlay.kind === "badge" ? overlay.text : label)
          }
          onClick={(e) => e.stopPropagation()}
          onBlur={(e) => commitRename(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitRename(e.currentTarget.value);
            } else if (e.key === "Escape") {
              setEditing(false);
            }
          }}
        />
      ) : (
        <span
          className="layer-name"
          onDoubleClick={(e) => { e.stopPropagation(); startRename(); }}
          title="Doppelklick zum Umbenennen"
        >
          {label}
          {isGroup && childCount > 0 ? (
            <span className="layer-name-meta"> · {childCount}</span>
          ) : null}
        </span>
      )}

      <span className="layer-time" title={`${overlay.fromSec.toFixed(1)}s · ${overlay.durSec.toFixed(1)}s`}>
        {overlay.fromSec.toFixed(1)}s
      </span>

      <button
        type="button"
        className="layer-action"
        title="Duplizieren"
        onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
        aria-label="Duplizieren"
      >
        ⎘
      </button>
      <button
        type="button"
        className="layer-action danger"
        title="Löschen"
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        aria-label="Löschen"
      >
        🗑
      </button>
    </div>
  );
};

/* ===================== scene row ===================== */

type SceneRowProps = {
  scene: ReelScene;
  index: number;
  meta?: SceneMeta;
  format: string;
  selected: boolean;
  hidden: boolean;
  locked: boolean;
  sfxMuted: boolean;
  musicMuted: boolean;
  label: string;
  headline: string;
  onSelect: () => void;
  onToggleHidden: () => void;
  onToggleLocked: () => void;
  onToggleSfx: () => void;
  onToggleMusic: () => void;
  onDuplicate?: () => void;
  onRemove?: () => void;
};

const SortableSceneRow: React.FC<SceneRowProps> = ({
  scene,
  index,
  meta,
  format,
  selected,
  hidden,
  locked,
  sfxMuted,
  musicMuted,
  label,
  headline,
  onSelect,
  onToggleHidden,
  onToggleLocked,
  onToggleSfx,
  onToggleMusic,
  onDuplicate,
  onRemove,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: `scene:${scene.uid}` });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  const cat = meta?.category ?? "visual";
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={[
        "scene-row",
        `scene-cat-${cat}`,
        selected ? "is-selected" : "",
        hidden ? "is-hidden" : "",
        locked ? "is-locked" : "",
      ].filter(Boolean).join(" ")}
      data-uid={scene.uid}
      onClick={onSelect}
      title="Szene auswählen"
    >
      <button
        type="button"
        className="layer-grip"
        title="Ziehen zum Umsortieren"
        aria-label="Drag-Handle"
        onClick={(e) => e.stopPropagation()}
        {...attributes}
        {...listeners}
      >
        ⋮⋮
      </button>
      <button
        type="button"
        className={`layer-eye ${hidden ? "is-off" : ""}`}
        onClick={(e) => { e.stopPropagation(); onToggleHidden(); }}
        title={hidden ? "Szene einblenden" : "Szene ausblenden"}
        aria-label={hidden ? "Einblenden" : "Ausblenden"}
      >
        {hidden ? "🚫" : "👁"}
      </button>
      <button
        type="button"
        className={`layer-lock ${locked ? "is-on" : ""}`}
        onClick={(e) => { e.stopPropagation(); onToggleLocked(); }}
        title={locked ? "Sperre aufheben" : "Szene sperren"}
        aria-label={locked ? "Entsperren" : "Sperren"}
      >
        {locked ? "🔒" : "🔓"}
      </button>
      <span className="scene-row-index">{String(index + 1).padStart(2, "0")}</span>
      <div className={`scene-thumb cat-${cat}`}>
        <LiveSceneThumb scene={scene} format={format} width={64} />
        <span className={`scene-thumb-cat cat-${cat}`} aria-hidden>
          {categoryIcon(cat)}
        </span>
      </div>
      <div className="scene-row-textcol">
        <span className="scene-row-label">{label}</span>
        {headline ? <span className="scene-row-sub">{truncate(headline, 36)}</span> : null}
      </div>
      <button
        type="button"
        className={`layer-action audio ${sfxMuted ? "is-muted" : ""}`}
        onClick={(e) => { e.stopPropagation(); onToggleSfx(); }}
        title={
          sfxMuted
            ? "Vorlagen-SFX dieser Scene wieder aktivieren"
            : "Vorlagen-SFX dieser Scene stumm (eigene SFX-Events bleiben hörbar)"
        }
        aria-label={sfxMuted ? "Vorlagen-SFX aktivieren" : "Vorlagen-SFX stumm"}
        aria-pressed={sfxMuted}
      >
        {sfxMuted ? "🔇" : "🔊"}
      </button>
      <button
        type="button"
        className={`layer-action audio ${musicMuted ? "is-muted" : ""}`}
        onClick={(e) => { e.stopPropagation(); onToggleMusic(); }}
        title={musicMuted ? "Musik in dieser Scene aktivieren" : "Musik in dieser Scene stumm"}
        aria-label={musicMuted ? "Musik aktivieren" : "Musik stumm"}
        aria-pressed={musicMuted}
      >
        {musicMuted ? "🚫" : "🎵"}
      </button>
      <span className="scene-row-time">{scene.durationSec.toFixed(1)}s</span>
      {onDuplicate ? (
        <button
          type="button"
          className="layer-action"
          title="Duplizieren"
          onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
          aria-label="Duplizieren"
        >
          ⎘
        </button>
      ) : null}
      {onRemove ? (
        <button
          type="button"
          className="layer-action danger"
          title="Löschen"
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          aria-label="Löschen"
        >
          🗑
        </button>
      ) : null}
    </div>
  );
};

/* ===================== main panel ===================== */

export const LayersPanel: React.FC<Props> = ({
  overlays,
  scenes,
  scenesCatalog,
  format,
  selectedUid,
  selectedUids,
  onSelect,
  onOverlayPatch,
  onReorderOverlays,
  onDuplicateOverlay,
  onRemoveOverlay,
  onBulkPatch,
  onBulkRemove,
  onBulkDuplicate,
  onGroup,
  onUngroup,
  onSeekSec,
  onReorderScenes,
  onPatchSceneExtras,
  onDuplicateScene,
  onRemoveScene,
}) => {
  /* ---------- compute display tree ----------
   * Photoshop convention: top of the list = front-most. So we sort each
   * bucket (root + per-group) by `track` DESC. Groups render their children
   * indented underneath when expanded. */
  const byUid = useMemo(() => new Map(overlays.map((o) => [o.uid, o] as const)), [overlays]);
  const childrenOf = useMemo(() => {
    const m = new Map<string, OverlayLayer[]>();
    for (const o of overlays) {
      if (!o.parentUid) continue;
      const list = m.get(o.parentUid) ?? [];
      list.push(o);
      m.set(o.parentUid, list);
    }
    for (const list of m.values()) list.sort((a, b) => b.track - a.track);
    return m;
  }, [overlays]);

  const rootLayers = useMemo(
    () => [...overlays].filter((o) => !o.parentUid).sort((a, b) => b.track - a.track),
    [overlays],
  );

  // Flatten tree into a stable visit order (top → bottom). Used as the
  // canonical list for SortableContext + Shift-click ranges.
  const visibleFlat = useMemo(() => {
    const out: { layer: OverlayLayer; indent: number }[] = [];
    const visit = (nodes: OverlayLayer[], indent: number) => {
      for (const n of nodes) {
        out.push({ layer: n, indent });
        if (n.kind === "group" && n.expanded !== false) {
          const kids = childrenOf.get(n.uid) ?? [];
          visit(kids, indent + 1);
        }
      }
    };
    visit(rootLayers, 0);
    return out;
  }, [rootLayers, childrenOf]);

  const orderedUids = useMemo(() => visibleFlat.map((r) => r.layer.uid), [visibleFlat]);

  /* ---------- effective hidden / locked cascade ---------- */
  const effectiveFlags = useMemo(() => {
    const map = new Map<string, { hidden: boolean; locked: boolean }>();
    for (const o of overlays) {
      let hidden = !!o.hidden;
      let locked = !!o.locked;
      let cur: OverlayLayer | undefined = o.parentUid ? byUid.get(o.parentUid) : undefined;
      const seen = new Set<string>();
      while (cur && !seen.has(cur.uid)) {
        if (cur.hidden) hidden = true;
        if (cur.locked) locked = true;
        seen.add(cur.uid);
        cur = cur.parentUid ? byUid.get(cur.parentUid) : undefined;
      }
      map.set(o.uid, { hidden, locked });
    }
    return map;
  }, [overlays, byUid]);

  /* ---------- DnD reorder ---------- */
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const activeId = String(active.id);
      const overId = String(over.id);
      const activeLayer = byUid.get(activeId);
      const overLayer = byUid.get(overId);
      if (!activeLayer || !overLayer) return;

      // Reordering only happens within the same "bucket" (same parentUid).
      // Cross-bucket drag (move into / out of a group) is a future feature;
      // for now we silently no-op so we don't accidentally re-parent.
      if ((activeLayer.parentUid ?? null) !== (overLayer.parentUid ?? null)) return;

      const bucketSorted =
        activeLayer.parentUid == null
          ? rootLayers
          : (childrenOf.get(activeLayer.parentUid) ?? []);
      const oldIndex = bucketSorted.findIndex((o) => o.uid === activeId);
      const newIndex = bucketSorted.findIndex((o) => o.uid === overId);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = arrayMove(bucketSorted, oldIndex, newIndex);
      const restacked = restackTracks(reordered);

      // Emit a flat overlays array with the bucket's tracks rewritten.
      const restackedByUid = new Map(restacked.map((o) => [o.uid, o] as const));
      const next = overlays.map((o) => restackedByUid.get(o.uid) ?? o);
      onReorderOverlays(next);
    },
    [byUid, rootLayers, childrenOf, overlays, onReorderOverlays],
  );

  /* ---------- click handler with Shift / Cmd ---------- */
  const handleRowSelect = useCallback(
    (uid: string, e: React.MouseEvent) => {
      const mode: "replace" | "toggle" | "range" = e.shiftKey
        ? "range"
        : e.metaKey || e.ctrlKey
          ? "toggle"
          : "replace";
      onSelect(uid, mode, orderedUids);
    },
    [onSelect, orderedUids],
  );

  /* ---------- bulk action toolbar ---------- */
  const selectedArray = useMemo(() => Array.from(selectedUids), [selectedUids]);
  const multiCount = selectedUids.size;
  const allHidden = selectedArray.length > 0 && selectedArray.every((u) => byUid.get(u)?.hidden);
  const allLocked = selectedArray.length > 0 && selectedArray.every((u) => byUid.get(u)?.locked);
  const canGroup = selectedArray.length >= 2 && selectedArray.every((u) => {
    const o = byUid.get(u);
    return o && o.kind !== "group" && !o.parentUid;
  });
  const canUngroup = selectedArray.some((u) => {
    const o = byUid.get(u);
    return o && (o.kind === "group" || !!o.parentUid);
  });

  /* ---------- scene labels ---------- */
  const sceneLabel = useCallback(
    (s: ReelScene): string => {
      const meta = scenesCatalog.find((m) => m.id === s.sceneId);
      return meta?.label ?? s.sceneId;
    },
    [scenesCatalog],
  );
  const sceneMeta = useCallback(
    (s: ReelScene): SceneMeta | undefined => scenesCatalog.find((m) => m.id === s.sceneId),
    [scenesCatalog],
  );

  /* ---------- scene DnD reorder ---------- */
  const sceneSortableIds = useMemo(
    () => scenes.map((s) => `scene:${s.uid}`),
    [scenes],
  );

  const onScenesDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id || !onReorderScenes) return;
      const fromUid = String(active.id).replace(/^scene:/, "");
      const toUid = String(over.id).replace(/^scene:/, "");
      const oldIndex = scenes.findIndex((s) => s.uid === fromUid);
      const newIndex = scenes.findIndex((s) => s.uid === toUid);
      if (oldIndex < 0 || newIndex < 0) return;
      const next = arrayMove(scenes, oldIndex, newIndex);
      onReorderScenes(next);
    },
    [scenes, onReorderScenes],
  );

  const sceneIsHidden = useCallback(
    (s: ReelScene): boolean => Boolean(s.inputs.extras?.hidden),
    [],
  );
  const sceneIsLocked = useCallback(
    (s: ReelScene): boolean => Boolean(s.inputs.extras?.locked),
    [],
  );
  /** Pro-Szene SFX-Mute folgt der bestehenden Konvention `extras.sfx === false`. */
  const sceneSfxMuted = useCallback(
    (s: ReelScene): boolean => s.inputs.extras?.sfx === false,
    [],
  );
  /** Pro-Szene Musik-Mute (neu): `extras.muteMusic === true`. */
  const sceneMusicMuted = useCallback(
    (s: ReelScene): boolean => s.inputs.extras?.muteMusic === true,
    [],
  );

  return (
    <div className="layers-panel">
      {/* ----- bulk action toolbar (only when 2+ selected) ----- */}
      {multiCount > 1 ? (
        <div className="layers-bulk-bar">
          <span className="layers-bulk-count">{multiCount} ausgewählt</span>
          <div className="layers-bulk-actions">
            <button
              type="button"
              className="layers-bulk-btn"
              title={allHidden ? "Alle einblenden" : "Alle ausblenden"}
              onClick={() => onBulkPatch(selectedArray, { hidden: !allHidden })}
            >
              {allHidden ? "👁 Zeigen" : "🚫 Verstecken"}
            </button>
            <button
              type="button"
              className="layers-bulk-btn"
              title={allLocked ? "Alle entsperren" : "Alle sperren"}
              onClick={() => onBulkPatch(selectedArray, { locked: !allLocked })}
            >
              {allLocked ? "🔓 Entsperren" : "🔒 Sperren"}
            </button>
            {canGroup ? (
              <button
                type="button"
                className="layers-bulk-btn primary"
                title="Auswahl gruppieren"
                onClick={() => onGroup(selectedArray)}
              >
                📁 Gruppieren
              </button>
            ) : null}
            {canUngroup ? (
              <button
                type="button"
                className="layers-bulk-btn"
                title="Aus Gruppe lösen / Gruppe auflösen"
                onClick={() => onUngroup(selectedArray)}
              >
                ⤴ Aus Gruppe
              </button>
            ) : null}
            <button
              type="button"
              className="layers-bulk-btn"
              title="Alle duplizieren"
              onClick={() => onBulkDuplicate(selectedArray)}
            >
              ⎘ Dupl.
            </button>
            <button
              type="button"
              className="layers-bulk-btn danger"
              title="Alle löschen"
              onClick={() => onBulkRemove(selectedArray)}
            >
              🗑 Löschen
            </button>
          </div>
        </div>
      ) : null}

      <div className="layers-section-head">
        <span className="layers-section-title">Overlays</span>
        <span className="layers-section-count">{overlays.length}</span>
      </div>

      {overlays.length === 0 ? (
        <div className="layers-empty">
          Noch keine Overlay-Layer.<br />
          Lege Texte, Shapes, Icons oder Bilder über <strong>+ T</strong> oder die
          Element-Library an.
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
        >
          <SortableContext items={orderedUids} strategy={verticalListSortingStrategy}>
            <div className="layers-list">
              {visibleFlat.map(({ layer: o, indent }) => {
                const eff = effectiveFlags.get(o.uid) ?? { hidden: false, locked: false };
                return (
                  <LayerRow
                    key={o.uid}
                    overlay={o}
                    indent={indent}
                    selected={selectedUids.has(o.uid) || selectedUid === o.uid}
                    effectivelyHidden={eff.hidden}
                    effectivelyLocked={eff.locked}
                    expanded={o.kind === "group" ? o.expanded !== false : undefined}
                    isGroup={o.kind === "group"}
                    childCount={o.kind === "group" ? (childrenOf.get(o.uid)?.length ?? 0) : 0}
                    onSelect={(e) => handleRowSelect(o.uid, e)}
                    onToggleHidden={() => onOverlayPatch(o.uid, { hidden: !o.hidden })}
                    onToggleLocked={() => onOverlayPatch(o.uid, { locked: !o.locked })}
                    onToggleExpanded={
                      o.kind === "group"
                        ? () => onOverlayPatch(o.uid, { expanded: !(o.expanded !== false) } as Partial<OverlayLayer>)
                        : undefined
                    }
                    onRename={(name) =>
                      onOverlayPatch(
                        o.uid,
                        o.kind === "text" || o.kind === "badge"
                          ? !o.name
                            ? ({ text: name, name } as Partial<OverlayLayer>)
                            : ({ name } as Partial<OverlayLayer>)
                          : ({ name } as Partial<OverlayLayer>),
                      )
                    }
                    onDuplicate={() => onDuplicateOverlay(o.uid)}
                    onRemove={() => onRemoveOverlay(o.uid)}
                    onSeekToStart={
                      onSeekSec ? () => onSeekSec(o.fromSec + 0.05) : undefined
                    }
                  />
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <div className="layers-section-head" style={{ marginTop: 18 }}>
        <span className="layers-section-title">Scenes</span>
        <span className="layers-section-count">{scenes.length}</span>
      </div>
      {scenes.length === 0 ? (
        <div className="layers-list scenes-list">
          <div className="layers-empty subtle">Keine Scenes — Library nutzen.</div>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onScenesDragEnd}
        >
          <SortableContext items={sceneSortableIds} strategy={verticalListSortingStrategy}>
            <div className="layers-list scenes-list">
              {scenes.map((s, i) => {
                const meta = sceneMeta(s);
                const headline =
                  (s.inputs.text as string | undefined) ||
                  (s.inputs.subtext as string | undefined) ||
                  (s.inputs.number as string | undefined) ||
                  (s.inputs.items?.[0] ?? "");
                return (
                  <SortableSceneRow
                    key={s.uid}
                    scene={s}
                    index={i}
                    meta={meta}
                    format={format}
                    selected={selectedUid === s.uid}
                    hidden={sceneIsHidden(s)}
                    locked={sceneIsLocked(s)}
                    sfxMuted={sceneSfxMuted(s)}
                    musicMuted={sceneMusicMuted(s)}
                    label={sceneLabel(s)}
                    headline={headline}
                    onSelect={() => onSelect(s.uid, "replace")}
                    onToggleHidden={() =>
                      onPatchSceneExtras?.(s.uid, { hidden: !sceneIsHidden(s) })
                    }
                    onToggleLocked={() =>
                      onPatchSceneExtras?.(s.uid, { locked: !sceneIsLocked(s) })
                    }
                    onToggleSfx={() =>
                      onPatchSceneExtras?.(s.uid, { muteSfx: !sceneSfxMuted(s) })
                    }
                    onToggleMusic={() =>
                      onPatchSceneExtras?.(s.uid, { muteMusic: !sceneMusicMuted(s) })
                    }
                    onDuplicate={onDuplicateScene ? () => onDuplicateScene(s.uid) : undefined}
                    onRemove={onRemoveScene ? () => onRemoveScene(s.uid) : undefined}
                  />
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <div className="layers-help">
        <strong>Tipps:</strong> Drag = umsortieren · Doppelklick = Umbenennen ·
        Shift-Klick = Bereich · ⌘/Ctrl-Klick = Mehrfach · 📁 = Gruppe (kapselt
        Sichtbarkeit + Sperre)
      </div>
    </div>
  );
};
